import { Button } from "@material-ui/core";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import LocalizationProvider from "@material-ui/lab/LocalizationProvider";
import { createBrowserHistory } from "history";
import React, { useEffect } from "react";
import ReactGA from "react-ga";
import { HelmetProvider } from "react-helmet-async";
import { hotjar } from "react-hotjar";
import { Router } from "react-router-dom";
// ...
import { RecoilRoot } from "recoil";

import ErrorBoundary from "./components/ErrorBoundary";
import GoogleAnalytics from "./components/GoogleAnalytics";
import NotistackProvider from "./components/NotistackProvider";
import RtlLayout from "./components/RtlLayout";
// ...
import ScrollToTop from "./components/ScrollToTop";
import routes, { renderRoutes } from "./routes";
// ...
import ThemeConfig from "./theme";

const REACT_GA_TRACKING_ID =
  process.env.REACT_APP_GA_TRACKING_ID || "UA-XXXXX-X";
ReactGA.initialize(REACT_GA_TRACKING_ID);
const history = createBrowserHistory();

//? La sortie secondaire de la barrière de coquille, et d'elle SEULE : c'est la
//? seule zone où la navigation de l'application n'a pas survécu, donc la seule où
//? un lien externe est la bonne réponse. Les autres zones laissent un menu debout
//? et n'ont besoin que de leur bouton.
//?
//? `REACT_APP_EVEY_URL` est la variable déjà utilisée partout ailleurs pour
//? désigner l'application appelante (`layout/DashboardSidebar.js:129`,
//? `layout/AccountPopover.js:18`, `views/EventAuth/index.js:158`). Si elle est
//? absente — aucun `.env` n'est livré dans ce dépôt —, on n'affiche RIEN plutôt
//? qu'un lien mort vers « undefined/events ».
//?
//? Une ancre, pas `history.push` : le tableau de bord est une AUTRE application
//? (`evey.next`), hors du routeur de celle-ci.
//?
//? ⚠️ LA RACINE, ET PAS UN CHEMIN PLUS PRÉCIS. Il serait tentant de viser
//? directement la liste d'événements de l'organisateur, mais ce chemin appartient
//? à `evey.next` — un module que je ne possède pas et dont je ne peux pas vérifier
//? le routage ici. Un lien de secours qui tombe en 404 est pire que pas de lien.
//? La racine est le seul chemin garanti, et l'organisateur y retrouve son tableau
//? de bord.
const ShellFallbackActions = () => {
  const LIVE_URL = process.env.REACT_APP_EVEY_URL;
  if (!LIVE_URL) return null;
  // ...
  return (
    <Button variant="outlined" href={LIVE_URL} sx={{ boxShadow: "none" }}>
      Back to Evey
    </Button>
  );
};

export default function App() {

  useEffect(() => {
    const trackPageView = () => {
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "PageView");
      }
    };

    trackPageView();
    const unlisten = history.listen(trackPageView);

    return () => {
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
  }, []);

  // ...
  useEffect(() => {
    try {
      hotjar.initialize(
        process.env.REACT_APP_HOTJAR_ID,
        process.env.REACT_APP_HOTJAR_SV
      );
    } catch (error) {
      console.error("Hotjar error : ", error);
    }
  }, []);
  // ...
  return (
      <HelmetProvider>
        <RtlLayout>
          <ThemeConfig>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <NotistackProvider>
                <RecoilRoot>
                  {/* ZONE « COQUILLE » — le dernier recours. Posée le 16/09/2026.

                      Elle n'attrape que ce qu'aucune barrière plus proche n'attrape :
                      le routeur lui-même, le `Guard` (qui lit Recoil), `Layout`
                      au-dessus de ses propres barrières, et tout ce qui tomberait
                      pendant le démarrage. Les zones intérieures — barre du haut, menu,
                      contenu de route, étape de l'assistant — ont chacune la leur, donc
                      celle-ci ne masque pas leurs plantages : elle ne voit que ce qui
                      leur échappe.

                      ⚠️ Rendue DANS `ThemeConfig` et `RecoilRoot`, volontairement : le
                      bloc d'erreur utilise MUI, et il doit pouvoir s'afficher avec le
                      thème monté. Une barrière posée au-dessus de `ThemeConfig`
                      rendrait un écran d'erreur non stylé — au moment précis où il faut
                      qu'il soit lisible.

                      Ici le cadre NE SURVIT PAS, par construction : c'est le cas où la
                      navigation elle-même est tombée. D'où la sortie secondaire
                      explicite vers le tableau de bord — compter sur un menu qui n'est
                      pas là, c'est laisser l'organisateur dans un cul-de-sac. Ce lien
                      est une navigation choisie, pas un rechargement automatique. */}
                  <ErrorBoundary
                    zone="app-shell"
                    title="The event setup workspace stopped responding"
                    message="Something failed outside the page itself, so the navigation could not be drawn. Nothing you had already saved has been lost. Try again below — if this screen comes back, reopen Setup from your organizer dashboard."
                    actions={<ShellFallbackActions />}>
                    <Router history={history}>
                      <ScrollToTop />
                      <GoogleAnalytics />
                      {renderRoutes(routes)}
                    </Router>
                  </ErrorBoundary>
                </RecoilRoot>
              </NotistackProvider>
            </LocalizationProvider>
          </ThemeConfig>
        </RtlLayout>
      </HelmetProvider>
  );
}
